import { useCallback, useRef } from 'react';
import PeriodSelectionModal from 'features/premium/components/PeriodSelectionModalNew';
import { getTierSubscriptions, useAvailableIapSubscriptions, usePurchase, } from 'features/premium/hooks';
import useFailedPaymentModal from 'features/premium/hooks/useFailedPaymentModal';
import useIAPSubscriptionsNotFoundError from 'features/premium/hooks/useIAPSubscriptionsNotFoundError';
import { selectAvailableIAPSubscriptionsWithPrices, selectShouldShowStripe, selectStripePriceMap, selectSubscriptionPrices, } from 'features/premium/selectors';
import { useDisableAndroidBackButton } from 'hooks';
import { useAppSelector, useAppStore } from 'store/hooks';
import Amplitude from 'utils/amplitude';
import Sentry from 'utils/sentry';
import usePremiumPlanChangeExperience from 'features/premium/hooks/usePremiumPlanChangeExperience';
export const useActivatePremiumTrialModal = ({ selectedTier, onUpgradeComplete, benefitId, analyticsFlow, }) => {
    useAvailableIapSubscriptions();
    const isPeriodSelectionOpenRef = useRef(false);
    useDisableAndroidBackButton(useCallback(() => {
        if (isPeriodSelectionOpenRef.current) {
            PeriodSelectionModal.hide(() => {
                isPeriodSelectionOpenRef.current = false;
            }, { top: true });
            isPeriodSelectionOpenRef.current = false;
            return true;
        }
        return false;
    }, []));
    const prices = useAppSelector(selectSubscriptionPrices);
    const [isFetching, subscribe] = usePurchase(async (productId, newSku, discount, proratedRefund) => {
        isPeriodSelectionOpenRef.current = true;
        onUpgradeComplete?.(productId, newSku, discount, proratedRefund);
    }, analyticsFlow, benefitId);
    const performSubscribe = useCallback((subscription, skipFreeTrial) => {
        Amplitude.logEvent('Open Subscription Price Sheet');
        return subscribe(subscription, undefined, skipFreeTrial);
    }, [subscribe]);
    const onError = useIAPSubscriptionsNotFoundError();
    const closedWithSelection = useRef(undefined);
    const store = useAppStore();
    const { startFlow, isFetching: isFetchingDiscountOrPreview } = usePremiumPlanChangeExperience();
    const { paymentFailed, showFailedPaymentModal, isFetching: isFetchingPayment, } = useFailedPaymentModal();
    // This shows the monthly | annual selection modal when tier is different to current trier
    const openModal = useCallback(async (subscriptions, retry, skipFreeTrial) => {
        if (paymentFailed) {
            showFailedPaymentModal(() => {
                openModalRef.current?.(subscriptions, retry, skipFreeTrial);
            });
            return;
        }
        const currentSubscriptions = subscriptions.filter((sub) => sub !== undefined);
        if (currentSubscriptions && currentSubscriptions.length > 0) {
            if (currentSubscriptions.length === 1) {
                await startFlow(currentSubscriptions[0], () => performSubscribe(currentSubscriptions[0], skipFreeTrial));
            }
            else {
                isPeriodSelectionOpenRef.current = true;
                PeriodSelectionModal.show({
                    subscriptions: currentSubscriptions,
                    onSelect: (subscription) => performSubscribe(subscription, skipFreeTrial),
                    onClose: (selection, onDidHideCallback) => {
                        closedWithSelection.current = selection;
                        PeriodSelectionModal.hide(onDidHideCallback, { top: true });
                    },
                    reopen: retry,
                    defaultSelection: closedWithSelection.current,
                }, {
                    top: true,
                    onDidHideCallback: () => {
                        isPeriodSelectionOpenRef.current = false;
                    },
                });
            }
        }
        else {
            onError({
                retry,
            });
            Sentry.logError({
                filename: 'ProAndPlusOnboardingComponent',
                message: 'Failed to open PeriodSelectionModal due to missing or empty subscriptions array',
            });
        }
    }, [
        onError,
        paymentFailed,
        performSubscribe,
        showFailedPaymentModal,
        startFlow,
    ]);
    const openModalRef = useRef(openModal);
    openModalRef.current = openModal;
    const onPressUpgrade = useCallback((skipFreeTrial) => {
        if (!selectedTier) {
            return;
        }
        const subscriptions = selectAvailableIAPSubscriptionsWithPrices(store.getState());
        const stripePriceMap = selectStripePriceMap(store.getState());
        const canUseStripe = selectShouldShowStripe(store.getState());
        const currentSubscriptions = getTierSubscriptions(subscriptions, selectedTier, canUseStripe, stripePriceMap);
        openModal(currentSubscriptions, () => onPressUpgrade(skipFreeTrial), skipFreeTrial);
    }, [openModal, selectedTier]);
    return {
        prices,
        isFetching: isFetching || isFetchingPayment,
        isFetchingStripePreview: isFetchingDiscountOrPreview,
        openUpgradeModal: onPressUpgrade,
        openUpgradeModalWithSubscriptions: openModal,
        subscribe, // I think we can remove this after flag cleanup
    };
};
